import { Turnstile } from '@marsidev/react-turnstile';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import cn from 'classnames';
import { Formik, Form, Field } from 'formik';
import get from 'lodash/get';
import { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { CSSTransition } from 'react-transition-group';
import { useAnalytics } from 'use-analytics';

import Button from 'components/common/Button';
import FormField from 'components/common/FormField';
import StripePaymentElement from 'components/common/forms/StripePaymentElement';
import Heading from 'components/common/Heading';
import SlideoutPortal from 'components/common/SlideoutPortal';
import Text from 'components/common/Text';
import { nullableAddressFormSchema } from 'components/shared/FormSchemas';
import {
  BasketContext,
  VoucherContext,
  PickYourMealsContext,
} from 'components/signUpFunnel/Contexts';
import { BrandContext, TEMPO } from 'context/BrandContext';
import localStorage from 'helpers/localStorage';
import * as Routes from 'helpers/routes';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useBrand from 'hooks/useBrand';
import useFeatureFlag from 'hooks/useFeatureFlag';
import useFunnelExperimentAssignment from 'hooks/useFunnelExperimentAssignment';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useFunnelNavigation from 'hooks/useFunnelNavigation';
import ApplePayLogo from 'images/logo/applePayCharcoalBg.svg';
import Paypal from 'images/logo/payPal.svg';
import {
  FAMILY,
  COMPLETE_GTM_EVENT,
  SIGN_UP_COMPLETE_EVENT,
  STRIPE,
  BRAINTREE,
  STRIPE_APPLE,
  INTRO_SMS_OPT_IN,
  SHOW_PROMO_BANNER_LOCAL_STORAGE,
  CLOUDFLARE_TURNSTILE_ENABLED,
} from 'lib/constants';
import {
  paymentError,
  paymentMethodCardErrorMessage,
  reauthenticatePaymentError,
  GENERIC_ERROR_MESSAGE,
} from 'lib/constants/paymentErrors';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

import AddressFields from './AddressFields';
import AddressFormSkeleton from './AddressFormSkeleton';
import ApplePay from './ApplePay';
import CheckoutSlideOut from './CheckoutSlideOut';
import ExpressCheckoutSection from './ExpressCheckoutSection';
import OrderSummary from './OrderSummary';
import { PaymentCallOut } from './PaymentCallOut';
import PaymentCoveredCallout from './PaymentCoveredCallout';
import Terms from './Terms';

const CheckoutForm = ({
  box,
  disabled,
  disableSubmit,
  handleZipCodeChange,
  mobileSignUp,
  setBox,
  setDisableSubmit,
  setToastMessage,
  setUser,
  user,
  zipCodeDeliverability,
}) => {
  const cloudflareTurnstileEnabled = useFeatureFlag(CLOUDFLARE_TURNSTILE_ENABLED);
  const { basket, setBasket } = useContext(BasketContext);
  const { brand } = useContext(BrandContext);
  const { nextStep } = useFunnelNavigation();
  const { voucher, setVoucher } = useContext(VoucherContext);
  const isTempo = useBrand(TEMPO);
  const { isPickYourMeals } = useContext(PickYourMealsContext);

  const [error, setError] = useState(null);
  // Provider-specific temporary payment credentials captured in the browser:
  // PayPal/Braintree returns a nonce, Stripe Apple Pay returns a PaymentMethod ID.
  const [payPalNonce, setPayPalNonce] = useState(null);
  const [applePayPaymentMethodId, setApplePayPaymentMethodId] = useState(null);

  const [creditCardErrorMessage, setCreditCardErrorMessage] = useState(null);
  const [hasIdMe, setHasIdMe] = useState(false);
  const [showIdMeError, setShowIdMeError] = useState(false);
  const [hasPromo, setHasPromo] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [deliveryWeeks, setDeliveryWeeks] = useState([]);
  const [shouldScrollToSubmit, setShouldScrollToSubmit] = useState(false);

  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { hash } = useLocation();
  const queryParams = new URLSearchParams(hash.substring(1));
  const accessToken = queryParams.get('access_token');
  const [showSlideout, setShowSlideout] = useState(false);

  const mealPlanSlug = useFunnelMealPlan();
  const isFamilyMealPlan = mealPlanSlug === FAMILY;
  const stripeSetups = useStripe();
  const elements = useElements();
  const formRef = useRef();
  const turnstileRef = useRef();
  const formErrorRef = useRef();
  const ccErrorRef = useRef();
  const transitionRef = useRef();
  const submitButtonRef = useRef();
  const isIdMeVoucher = voucher?.isIdMeMilitaryHealthTeacher || voucher?.isIdMeStudentAlumniGov;
  const experimentAssignment = useFunnelExperimentAssignment();
  const refreshDeliveryOptions = useCallback(
    async (values, formikApi) => {
      try {
        setDisableSubmit(true);
        const response = await RequestService.getDeliveryOptions({
          params: { zipCode: values.zipCode, pickYourMeals: isPickYourMeals },
        });
        if (response.ok && response.deliveryOptions) {
          const newDeliveryWeeks = response.deliveryOptions.firstDeliveryWeeks;
          setDeliveryWeeks(newDeliveryWeeks);
          const firstDeliveryDate = get(newDeliveryWeeks, '0.firstDeliveryDays.0.value', '');
          formikApi.resetForm({ values: { ...values, firstDeliveryDate } });
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      } finally {
        setDisableSubmit(false);
      }
    },
    [isPickYourMeals, setDisableSubmit]
  );

  useEffect(() => {
    if (!shouldScrollToSubmit) return;

    submitButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setShouldScrollToSubmit(false);
  }, [shouldScrollToSubmit]);

  const setPayPalDetails = useCallback(
    paypalDetails => {
      if (paypalDetails.token) {
        setPayPalNonce(paypalDetails.token);
        const paypalAddress = paypalDetails?.details?.shippingAddress;
        const normalizeZipCode = paypalAddress?.postalCode.replace(/\D/g, '').slice(0, 5);
        setUser(prevPerson => ({
          ...prevPerson,
          name: `${paypalDetails?.details?.firstName} ${paypalDetails?.details?.lastName}`,
          paypalEmail: paypalDetails?.details?.email,
          address: {
            streetAddress: paypalAddress?.line1 || '',
            streetAddress2: paypalAddress?.line2 || '',
          },
          phone: paypalDetails?.details?.phone,
          vendor: BRAINTREE,
          signupCity: paypalAddress?.city,
          signupState: paypalAddress?.state,
          signupZip: normalizeZipCode,
        }));
        setShouldScrollToSubmit(true);
      } else {
        setError(paymentError('PayPal'));
      }
    },
    [setUser]
  );

  const onPaypalClick = useAnalyticsCallback(
    () => {},
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].billing.paypalClicked, {
            mealPlanSlug: TEMPO,
            ...experimentAssignment,
          });
        } else {
          logEvent(events.signUp[brand].checkout.paypalClicked, experimentAssignment);
        }
      }
    },
    [brand, isPickYourMeals, experimentAssignment]
  );

  const onApplePayClick = useAnalyticsCallback(
    () => {
      const shippingCents = box?.shippingCents ?? 0;

      return {
        shippingRates: [
          {
            id: 'standard-shipping',
            displayName: shippingCents === 0 ? 'Free Shipping' : 'Standard Shipping',
            amount: shippingCents,
          },
        ],
      };
    },
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].billing.applepayClicked, {
            mealPlanSlug: TEMPO,
            ...experimentAssignment,
          });
        } else {
          logEvent(events.signUp[brand].checkout.applepayClicked, experimentAssignment);
        }
      }
    },
    [brand, isPickYourMeals, box?.shippingCents, experimentAssignment]
  );

  const setApplePayDetails = useCallback(
    expressData => {
      const { paymentMethod, billingDetails, shippingAddress, expressPaymentType } = expressData;
      setError(null);

      if (expressPaymentType !== 'apple_pay') {
        setApplePayPaymentMethodId(null);
        setError(paymentError('Apple Pay'));
        return;
      }

      const paymentMethodCardError = paymentMethodCardErrorMessage(paymentMethod);
      if (paymentMethodCardError) {
        setApplePayPaymentMethodId(null);
        setUser(prevPerson => ({
          ...prevPerson,
          vendor: STRIPE,
          applePayEmail: null,
        }));
        setError(paymentMethodCardError);
        return;
      }

      if (paymentMethod?.id) {
        setApplePayPaymentMethodId(paymentMethod.id);
        const email = billingDetails?.email || '';
        setUser(prevPerson => ({
          ...prevPerson,
          name: shippingAddress?.name || '',
          applePayEmail: email,
          address: {
            streetAddress: shippingAddress?.address?.line1 || '',
            streetAddress2: shippingAddress?.address?.line2 || '',
          },
          phone: shippingAddress?.phone || billingDetails?.phone || '',
          vendor: STRIPE_APPLE,
          signupCity: shippingAddress?.address?.city || '',
          signupState: shippingAddress?.address?.state || '',
          signupZip: shippingAddress?.address?.postal_code || '',
        }));
        setShouldScrollToSubmit(true);
      } else {
        setApplePayPaymentMethodId(null);
        setError(paymentError('Apple Pay'));
      }
    },
    [setUser]
  );

  const onApplePayError = useCallback(error => {
    ErrorLogging.captureException(error?.message || paymentError('Apple Pay'), {
      source: 'StripeExpressCheckout',
    });
  }, []);

  const trackAnalytics = useCallback(
    checkoutResponse => {
      try {
        const { weeklyBasket, user } = checkoutResponse;
        const credit = parseFloat(weeklyBasket.credit?.replace('$', '') ?? 0);
        const value = weeklyBasket.totalCents / 100;
        RequestService.v3.createEvents({
          params: {
            eventName: SIGN_UP_COMPLETE_EVENT,
            email: user.email,
            phone: user.phone,
            uuid: user.uuid,
            id: user.id,
            value,
            basketSlug: weeklyBasket.slug,
            basketId: weeklyBasket.id,
          },
        });
        analytics.track(COMPLETE_GTM_EVENT, {
          value,
          credit: credit,
          basket: weeklyBasket,
          voucherCode: user.signupVoucher,
          user: user,
          orderSubtotal: weeklyBasket.subtotalCents / 100,
        });
      } catch (e) {
        ErrorLogging.captureException(e.message);
      }
    },
    [analytics]
  );

  const handleCreateCheckoutResponse = useCallback(
    (createCheckoutResponse, values, formikApi) => {
      if (createCheckoutResponse.ok) {
        const { total, deliveryDate, editingCutoff } = createCheckoutResponse.weeklyBasket;
        setBasket({ ...basket, total, deliveryDate, editingCutoff });
        trackAnalytics(createCheckoutResponse);
        localStorage.delete(SHOW_PROMO_BANNER_LOCAL_STORAGE);
        localStorage.delete(INTRO_SMS_OPT_IN);
        const navigationState = {
          state: {
            user: {
              ...createCheckoutResponse.user,
              accessToken: createCheckoutResponse.accessToken,
            },
          },
        };
        if (isPickYourMeals) {
          navigate(Routes.PICK_MEALS_THANK_YOU_PATH, navigationState);
        } else {
          navigate(nextStep.route, navigationState);
        }
      } else {
        if (createCheckoutResponse.error) setError(createCheckoutResponse.error);
        setPayPalNonce(null);
        if (values.vendor === STRIPE_APPLE) {
          // Apple Pay payment method IDs are single-use in this flow; clear stored auth state on
          // backend checkout failure so the user must re-authenticate with Apple Pay before retrying.
          setApplePayPaymentMethodId(null);
          setUser(prevPerson => ({
            ...prevPerson,
            applePayEmail: null,
          }));
        }
        refreshDeliveryOptions(values, formikApi);
      }
    },
    [
      isPickYourMeals,
      basket,
      navigate,
      refreshDeliveryOptions,
      setUser,
      setBasket,
      trackAnalytics,
      nextStep,
    ]
  );

  const onSubmit = useAnalyticsCallback(
    async (values, formikApi) => {
      setError(null);
      let turnstileResponse;

      if (cloudflareTurnstileEnabled) {
        turnstileResponse = await turnstileRef.current?.getResponsePromise();
        if (!turnstileResponse) {
          ErrorLogging.captureException('Cloudflare turnstile token missing');
          setError(GENERIC_ERROR_MESSAGE);
          return;
        }
      }

      try {
        if (values.vendor === STRIPE_APPLE) {
          // Apple Pay
          if (!applePayPaymentMethodId && !process.env.CYPRESS) {
            setError(reauthenticatePaymentError('Apple Pay'));
            return;
          }
          const createCheckoutResponse = await RequestService.createCheckout({
            params: {
              ...values,
              paymentMethodId: applePayPaymentMethodId,
              applePayEmail: user.applePayEmail,
              'cf-turnstile-response': turnstileResponse,
            },
          });
          handleCreateCheckoutResponse(createCheckoutResponse, values, formikApi);
        } else if (values.vendor === BRAINTREE) {
          if (!payPalNonce && !process.env.CYPRESS) {
            setError(reauthenticatePaymentError('PayPal'));
            return;
          }
          const createCheckoutResponse = await RequestService.createCheckout({
            params: {
              ...values,
              vendor: BRAINTREE,
              cardToken: payPalNonce,
              paypalEmail: user.paypalEmail,
              'cf-turnstile-response': turnstileResponse,
            },
          });
          handleCreateCheckoutResponse(createCheckoutResponse, values, formikApi);
        } else if (values.vendor === STRIPE) {
          if (!elements) {
            setError(GENERIC_ERROR_MESSAGE);
            return;
          }
          const { error: submitError } = await elements.submit();
          if (submitError) {
            setError(submitError.message);
            return;
          }

          const { paymentMethod, error: paymentMethodError } =
            await stripeSetups.createPaymentMethod({
              elements,
              params: {
                billing_details: { address: { country: 'US', postal_code: values.zipCode } },
              },
            });
          const paymentMethodCardError = paymentMethodCardErrorMessage(paymentMethod);

          if (paymentMethodError || paymentMethodCardError) {
            setError(paymentMethodError?.message || paymentMethodCardError);
            return;
          }
          const createCheckoutResponse = await RequestService.createCheckout({
            params: {
              ...values,
              paymentMethodId: paymentMethod.id,
              'cf-turnstile-response': turnstileResponse,
            },
          });
          handleCreateCheckoutResponse(createCheckoutResponse, values, formikApi);
        } else {
          const createCheckoutResponse = await RequestService.createCheckout({
            params: { ...values, 'cf-turnstile-response': turnstileResponse },
          });
          handleCreateCheckoutResponse(createCheckoutResponse, values, formikApi);
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
        setError(GENERIC_ERROR_MESSAGE);
      }
    },
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].billing.submitted, {
            mealPlanSlug: TEMPO,
            numberMealsPicked: user?.weeklyMeals,
            promo: voucher?.code,
            ...experimentAssignment,
          });
        } else {
          logEvent(events.signUp[brand].checkout.submitted, {
            mealPlanSlug: mealPlanSlug,
            weeklyMeals: user?.weeklyMeals,
            servings: user?.servings,
            promoCode: voucher?.code,
            ...experimentAssignment,
          });
        }
      }
    },
    [
      user,
      isPickYourMeals,
      brand,
      mealPlanSlug,
      elements,
      stripeSetups,
      payPalNonce,
      isFamilyMealPlan,
      turnstileRef,
      cloudflareTurnstileEnabled,
      applePayPaymentMethodId,
      experimentAssignment,
    ]
  );

  useEffect(() => {
    setHasIdMe(user?.idMeData?.idMeUser);
  }, [user?.idMeData?.idMeUser]);

  useEffect(() => {
    if (user?.isAmex || user?.isUsaa || (hasIdMe && user?.signupVoucher) || voucher) {
      setHasPromo(true);
    }
  }, [voucher, user, hasIdMe]);

  useEffect(() => {
    const verifyIdMe = async () => {
      try {
        const response = await RequestService.idMeVerification({ params: { token: accessToken } });

        if (response.ok && response.verified) {
          if (response.signupVoucher && response.signupVoucher.isIdMeMilitaryHealthTeacher) {
            setHasIdMe(true);
          }
          setVoucher({ ...response.signupVoucher });

          if (response.signupVoucher) {
            setShowSlideout(true);
            setHasPromo(true);

            try {
              const boxResponse = await RequestService.getBox({
                params: {
                  servings: user.servings,
                  weeklyMeals: user.weeklyMeals,
                  zipCode: user.signupZip,
                  estimated: false,
                },
              });
              if (boxResponse.ok) {
                setBox(prevBox => ({ ...prevBox, ...boxResponse.box }));
              }
            } catch (error) {
              ErrorLogging.captureException(error.message);
              setShowSlideout(true);
            }
          }
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
        setShowIdMeError(true);
        setShowSlideout(true);
      }
    };
    if (accessToken && user) {
      verifyIdMe();
    }
  }, [accessToken, setBox, setVoucher, user]);

  const PaypalVendor = () => {
    return (
      <div className="sentry-mask my-2 rounded border border-charcoal-60 p-4">
        <img src={Paypal} alt="PayPal" className="mx-auto mb-2 h-10" />
        <Text>{user?.name}</Text>
        <Text>{user?.paypalEmail}</Text>
        <Button
          type="button"
          className="btnOutlined text border-gray-300 text mt-2 border border-solid border-charcoal-60 p-3"
          variation="Secondary"
          onClick={() =>
            // This click can race with other setUser calls (address/payment updates).
            // Using prev state avoids spreading a stale `user` snapshot and reverting newer fields.
            setUser(prevPerson => ({
              ...prevPerson,
              vendor: STRIPE,
              paypalEmail: null,
            }))
          }
        >
          Use a Credit Card Instead
        </Button>
      </div>
    );
  };

  const ApplePayVendor = () => {
    return (
      <div className="sentry-mask my-2 rounded border border-charcoal-60 p-4">
        <img src={ApplePayLogo} alt="Apple Pay" className="mx-auto mb-2 h-10" />
        <Text>{user?.name}</Text>
        <Text>{user?.applePayEmail}</Text>
        <Button
          type="button"
          className="btnOutlined text border-gray-300 text mt-2 border border-solid border-charcoal-60 p-3"
          variation="Secondary"
          onClick={() => {
            setApplePayPaymentMethodId(null);
            // This click can race with other setUser calls (address/payment updates).
            // Using prev state avoids spreading a stale `user` snapshot and reverting newer fields.
            setUser(prevPerson => ({
              ...prevPerson,
              vendor: STRIPE,
              applePayEmail: null,
            }));
          }}
        >
          Use a Credit Card Instead
        </Button>
      </div>
    );
  };

  const onStripePaymentFormChange = useCallback(
    event => {
      if (event.error) {
        setCreditCardErrorMessage(event.error.message);
      } else if (event.value && event.value.type) {
        const currentRef = formRef.current;
        currentRef.setFieldValue('vendor', STRIPE);
        setCreditCardErrorMessage(null);
      } else {
        setCreditCardErrorMessage(null);
      }
    },
    [formRef]
  );

  const setAddressDetails = addressDetails => {
    const currentRef = formRef.current;
    currentRef.setFieldValue('streetAddress', addressDetails?.streetAddress);
    currentRef.setFieldValue('city', addressDetails?.city);
    currentRef.setFieldValue('state', addressDetails?.state);
    currentRef.setFieldValue('zipCode', addressDetails?.zipCode);
  };

  const firstDeliveryDays = deliveryWeeks[0]?.firstDeliveryDays;
  const showPaymentOptions =
    box && (!box.promoApplied || (box.promoApplied && box.promo.paymentRequired));

  const submitButtonTextFor = ({ vendor, isPickYourMeals }) => {
    if (vendor === STRIPE_APPLE) return <ApplePay />;
    if (isPickYourMeals) return 'Sign Up';
    return 'Sign Up & Pick Meals';
  };

  const handleApplyButtonClickWithAnalytics = useAnalyticsCallback(
    async params => {
      setDisableSubmit(true);
      const response = await RequestService.addVoucher({ params });
      if (response.ok) {
        setVoucher(response.signupVoucher);
        setHasPromo(true);
        setErrorMessage();

        try {
          const boxResponse = await RequestService.getBox({
            params: {
              servings: user.servings,
              weeklyMeals: user.weeklyMeals,
              zipCode: user.signupZip,
              estimated: false,
            },
          });
          if (boxResponse.ok) {
            setBox(prevBox => ({ ...prevBox, ...boxResponse.box }));
          }
        } catch (error) {
          ErrorLogging.captureException(error.message);
        }
      } else {
        setHasPromo(false);
        setErrorMessage(response.error);
        setShowSlideout(true);
      }

      setDisableSubmit(false);
      localStorage.delete(SHOW_PROMO_BANNER_LOCAL_STORAGE);
    },
    ({ logEvent, events }) => {
      if (brand) {
        logEvent(events.signUp[brand].promo.checkoutPromoApplied, experimentAssignment);
      }
    },
    [
      brand,
      isPickYourMeals,
      setVoucher,
      setHasPromo,
      setErrorMessage,
      user,
      setBox,
      experimentAssignment,
    ]
  );

  if (!user || !box) return <AddressFormSkeleton />;

  return (
    <>
      <SlideoutPortal show={showSlideout}>
        {unmountSlideout => (
          <CheckoutSlideOut
            showSlideout={showSlideout}
            setShowSlideout={setShowSlideout}
            handleApplyButtonClickWithAnalytics={handleApplyButtonClickWithAnalytics}
            hasPromo={hasPromo}
            isIdMeVoucher={isIdMeVoucher}
            errorMessage={errorMessage}
            transitionRef={transitionRef}
            setHasPromo={setHasPromo}
            setErrorMessage={setErrorMessage}
            user={user}
            voucher={voucher}
            hasIdMe={hasIdMe}
            showIdMeError={showIdMeError}
            isFamilyMealPlan={isFamilyMealPlan}
            unmountSlideout={unmountSlideout}
          />
        )}
      </SlideoutPortal>
      <Formik
        innerRef={formRef}
        initialValues={{
          fullName: user.name || '',
          streetAddress: user.address.streetAddress || '',
          apartmentNumber: user?.address.streetAddress2 || '',
          city: user.signupCity || '',
          zipCode: user.signupZip || '',
          state: user.signupState || '',
          phoneNumber: user?.phone || '',
          firstDeliveryDate:
            box.deliveryDate || (firstDeliveryDays ? firstDeliveryDays[0].value : ''),
          termsChecked: user.acceptedAllAgreements || false,
          vendor: showPaymentOptions ? user.vendor || STRIPE : '',
          smsChecked: user.receivesSmsPromotional || false,
          mobileSignUp,
          password: '',
        }}
        validationSchema={nullableAddressFormSchema}
        onSubmit={onSubmit}
        validateOnBlur
        enableReinitialize={user.vendor === BRAINTREE}
      >
        {({ values, touched, errors, isSubmitting }) => (
          <Form aria-label="checkout form">
            <div
              className={cn('flex flex-col-reverse justify-center bp3:flex-row bp3:gap-24', {
                'bg-tempo-gray-40': isTempo && isPickYourMeals,
              })}
            >
              <div className="mx-6 mt-6 bp3:mx-0 bp3:mt-0 bp3:min-w-0 bp3:flex-1">
                <AddressFields
                  user={user}
                  deliveryWeeks={deliveryWeeks}
                  handleZipCodeChange={handleZipCodeChange}
                  zipCodeDeliverability={zipCodeDeliverability}
                  setAddressDetails={setAddressDetails}
                  showPaymentOptions={Boolean(showPaymentOptions)}
                  setBox={setBox}
                  disableSubmit={disableSubmit}
                  setDisableSubmit={setDisableSubmit}
                  setToastMessage={setToastMessage}
                  refreshDeliveryOptions={refreshDeliveryOptions}
                  mobileSignUp={mobileSignUp}
                >
                  <ExpressCheckoutSection
                    showPaymentOptions={Boolean(showPaymentOptions)}
                    setPayPalDetails={setPayPalDetails}
                    onPaypalClick={onPaypalClick}
                    onApplePayClick={onApplePayClick}
                    onApplePayComplete={setApplePayDetails}
                    onApplePayError={onApplePayError}
                  />
                </AddressFields>
                <div className="mx-auto w-full">
                  <section
                    className={cn(
                      'flex flex-col gap-4 rounded-2xl border',
                      isTempo ? 'border-charcoal-60' : 'border-cream-120'
                    )}
                  >
                    <Heading brandStylesOverrides="h4" className="mb-0">
                      Payment
                    </Heading>
                    {showPaymentOptions ? (
                      <>
                        <PaymentCallOut
                          editingCutoff={box?.editingCutoff}
                          vendor={values.vendor}
                          box={box}
                          firstDeliveryDays={firstDeliveryDays}
                        />
                        {values.vendor === BRAINTREE ? (
                          <PaypalVendor />
                        ) : values.vendor === STRIPE_APPLE ? (
                          <ApplePayVendor />
                        ) : (
                          <>
                            <StripePaymentElement
                              defaultValues={{ zipCode: values.zipCode, email: user.email }}
                              onChange={onStripePaymentFormChange}
                              variant="refreshed"
                            />
                            <CSSTransition
                              in={Boolean(creditCardErrorMessage)}
                              classNames="fadeInOut"
                              timeout={200}
                              unmountOnExit
                              nodeRef={ccErrorRef}
                            >
                              <div ref={ccErrorRef}>
                                <Text brandStylesOverrides="text text-negative" className="pb-4">
                                  {creditCardErrorMessage}
                                </Text>
                              </div>
                            </CSSTransition>
                          </>
                        )}
                      </>
                    ) : (
                      <PaymentCoveredCallout />
                    )}
                  </section>
                </div>
                <Field name="mobileSignUp" type="hidden" />
                {mobileSignUp && (
                  <FormField
                    name="password"
                    autoComplete="off"
                    label="Create a Password"
                    inputClass="w-full bg-white-100 shadow rounded-md mt-4 p-4"
                    containerClass="pt-4"
                    type="password"
                    error={touched.password && errors.password}
                  />
                )}
                <Terms values={values} user={user} box={box} />
                <CSSTransition
                  in={Boolean(error)}
                  classNames="fadeInOut"
                  timeout={200}
                  unmountOnExit
                  nodeRef={formErrorRef}
                >
                  <div ref={formErrorRef} className="mr-auto w-full max-w-3xl p-4">
                    <Text brandStylesOverrides="text text-negative">{error}</Text>
                  </div>
                </CSSTransition>
                {cloudflareTurnstileEnabled && (
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={process.env.CLOUDFLARE_TURNSTILE_SITE_KEY}
                    options={{ appearance: 'interaction-only' }}
                    onExpire={() => turnstileRef.current?.reset()}
                    onError={errorCode => {
                      ErrorLogging.captureException('Cloudflare turnstile error', {
                        code: errorCode,
                      });
                      setError(GENERIC_ERROR_MESSAGE);
                    }}
                  />
                )}
                <div ref={submitButtonRef} className="mx-auto mt-6 w-full">
                  <Button
                    type="submit"
                    className={cn('text-md w-full font-medium', {
                      'border-black-100 bg-black-100 text-white-100 hover:bg-black-100 hover:opacity-80 focus:bg-black-100 focus:opacity-80':
                        values.vendor === STRIPE_APPLE,
                    })}
                    isSubmitting={isSubmitting}
                    disabled={disabled || disableSubmit}
                    submittingText={
                      disableSubmit
                        ? submitButtonTextFor({ vendor: values.vendor, isPickYourMeals })
                        : 'Saving...'
                    }
                    variation="Pill"
                  >
                    {submitButtonTextFor({ vendor: values.vendor, isPickYourMeals })}
                  </Button>
                </div>
              </div>
              <OrderSummary
                user={user}
                box={box}
                setShowSlideout={setShowSlideout}
                voucher={voucher}
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

CheckoutForm.propTypes = {
  user: PropTypes.object,
  setUser: PropTypes.func,
  handleZipCodeChange: PropTypes.func,
  zipCodeDeliverability: PropTypes.object,
  disableSubmit: PropTypes.bool.isRequired,
  setDisableSubmit: PropTypes.func.isRequired,
  box: PropTypes.shape({
    subtotalFormatted: PropTypes.string.isRequired,
    subtotalCents: PropTypes.number.isRequired,
    totalFormatted: PropTypes.string.isRequired,
    totalCents: PropTypes.number.isRequired,
    promoApplied: PropTypes.bool.isRequired,
    shippingFormatted: PropTypes.string.isRequired,
    shippingCents: PropTypes.number.isRequired,
    taxFormatted: PropTypes.string.isRequired,
    taxCents: PropTypes.number.isRequired,
    pricePerServingFormatted: PropTypes.string.isRequired,
    pricePerServingCents: PropTypes.number.isRequired,
    afterSignUpTotalFormatted: PropTypes.string.isRequired,
    afterSignUpTotalCents: PropTypes.number.isRequired,
    promo: PropTypes.shape({
      subtotalCents: PropTypes.number,
      subtotalFormatted: PropTypes.string,
      paymentRequired: PropTypes.bool,
    }),
    editingCutoff: PropTypes.string.isRequired,
    deliveryDate: PropTypes.string.isRequired,
  }),
  disabled: PropTypes.bool.isRequired,
  setBox: PropTypes.func,
  mobileSignUp: PropTypes.bool.isRequired,
  setToastMessage: PropTypes.func.isRequired,
};

export default CheckoutForm;
